import { z } from 'zod';

// The two links that need no login: the door check-in (/api/checkin/:token) and a committee
// member's one-use availability poll (/api/availability/:token). The token itself is checked by
// the services (format, hash lookup, validity window) so the pages keep their own messages.

export const CheckinPerson = z.object({
  id: z.string(),
  name: z.string(),
  affiliation: z.string().nullable(),
  status: z.string(),
  checkedIn: z.array(z.string()),
});
export type CheckinPerson = z.infer<typeof CheckinPerson>;

export const CheckinView = z.object({
  title: z.string(),
  titleFr: z.string(),
  venue: z.string().nullable(),
  days: z.array(z.object({ date: z.string(), label: z.string().nullable() })),
  today: z.string().nullable(),
  roster: z.array(CheckinPerson),
});
export type CheckinView = z.infer<typeof CheckinView>;

export const CheckinSearchQuery = z.strictObject({ q: z.string().max(200).optional() });
export type CheckinSearchQuery = z.input<typeof CheckinSearchQuery>;

export const CheckinSearchResponse = z.object({ results: z.array(CheckinPerson) });
export type CheckinSearchResponse = z.infer<typeof CheckinSearchResponse>;

/**
 * The page's offline queue. Each entry is checked on its own by the service (a bad entry is
 * reported as rejected, the rest are still recorded), so a replayed queue never stalls on one tap.
 */
export const CheckinRequest = z.strictObject({ entries: z.array(z.unknown()) });
export type CheckinRequest = z.input<typeof CheckinRequest>;

export const CheckinEntry = z.object({
  clientId: z.string(),
  day: z.string().optional(),
  at: z.string().optional(),
  inviteeId: z.string().optional(),
  code: z.string().optional(),
  walkIn: z.object({ name: z.string(), email: z.string(), affiliation: z.string() }).optional(),
});
export type CheckinEntry = z.infer<typeof CheckinEntry>;

export const CheckinResult = z.object({
  clientId: z.string().nullable(),
  status: z.enum(['checked_in', 'already', 'not_found', 'rejected']),
  reason: z.string().optional(),
  person: CheckinPerson.optional(),
});
export type CheckinResult = z.infer<typeof CheckinResult>;

export const CheckinResponse = z.object({ results: z.array(CheckinResult), recorded: z.number() });
export type CheckinResponse = z.infer<typeof CheckinResponse>;

export const PollSlot = z.object({ id: z.string(), startsAt: z.string(), endsAt: z.string().nullable() });
export type PollSlot = z.infer<typeof PollSlot>;

export const PollView = z.object({
  title: z.string(),
  committee: z.string(),
  committeeFr: z.string(),
  slots: z.array(PollSlot),
  open: z.boolean(),
  answered: z.boolean(),
});
export type PollView = z.infer<typeof PollView>;

/** An empty list is a valid answer (none of the times work); a missing list is not. */
export const PollAnswerRequest = z.strictObject({ choices: z.array(z.string().max(100)).max(500) });
export type PollAnswerRequest = z.input<typeof PollAnswerRequest>;

export const PollAnswerResponse = z.object({ answered: z.literal(true) });
export type PollAnswerResponse = z.infer<typeof PollAnswerResponse>;
